// PlantWaterReset.jsx
// Component for resetting a plant's water needs by setting lastWater to 1 month in the past

import React, { useState } from "react";
import { updateDB, getPlantFromDatabase } from "../../DatabaseOperations";
import { Timestamp } from "firebase/firestore";

// React Components
import ConsoleHomePageHeader from "../Home/ConsoleHomePageHeader";

// CSS
import "../SpeciesInfoUpload/PlantEditor.css";

const PlantWaterReset = () => {
  // Hooks for form data
  const [plantId, setPlantId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [plantInfo, setPlantInfo] = useState(null);

  const validatePlant = async () => {
    if (!plantId.trim()) {
      setError("Please enter a plant ID");
      return;
    }

    setLoading(true);
    setError("");
    setPlantInfo(null);

    try {
      // First, verify the plant exists and get its info
      const plant = await getPlantFromDatabase(plantId.trim());
      setPlantInfo({
        name: plant.name || "Unnamed Plant",
        species: plant.species,
        currentLastWater: plant.lastWater,
      });
    } catch (err) {
      setError(`Plant not found: ${err}`);
      console.error("Error finding plant:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetWaterNeeds = async () => {
    if (!plantId.trim()) {
      setError("Please enter a plant ID");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      // Create a timestamp for 1 month ago (30 days)
      const oneMonthAgo = new Date();
      oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);
      const pastTimestamp = Timestamp.fromDate(oneMonthAgo);

      // Update the plant document
      await updateDB(plantId.trim(), "Plants", {
        lastWater: pastTimestamp,
      });

      setResult({
        plantId: plantId.trim(),
        resetDate: oneMonthAgo.toLocaleDateString(),
        resetTime: oneMonthAgo.toLocaleTimeString(),
      });

      // Clear plant info after successful reset
      setPlantInfo(null);
    } catch (err) {
      setError(`Failed to reset water needs: ${err.message}`);
      console.error("Error resetting water needs:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPlantId("");
    setPlantInfo(null);
    setResult(null);
    setError("");
  };

  const formatFirebaseTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";

    try {
      // Handle both Timestamp objects and plain objects with seconds
      const date = timestamp.toDate
        ? timestamp.toDate()
        : new Date(timestamp.seconds * 1000);
      return `${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
    } catch (err) {
      return "Invalid date";
    }
  };

  return (
    <div>
      <ConsoleHomePageHeader />
      <div className="title">Plant Water Reset</div>

      <div style={{ maxWidth: "600px", margin: "0 auto", padding: "20px" }}>
        <div style={{ marginBottom: "20px" }}>
          <p style={{ color: "#666", fontSize: "14px", marginBottom: "15px" }}>
            This tool sets a plant's <code>lastWater</code> timestamp to 1 month
            ago, making the plant appear as if it needs watering.
          </p>

          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
            }}
          >
            Plant ID:
          </label>
          <input
            type="text"
            placeholder="Enter plant document ID"
            value={plantId}
            onChange={(e) => setPlantId(e.target.value)}
            onBlur={validatePlant}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              marginBottom: "10px",
            }}
          />
        </div>

        {plantInfo && (
          <div
            style={{
              padding: "15px",
              backgroundColor: "#f0f8ff",
              borderRadius: "8px",
              border: "1px solid #b3d9ff",
              marginBottom: "20px",
            }}
          >
            <h4 style={{ margin: "0 0 10px 0", color: "#1976d2" }}>
              Plant Found:
            </h4>
            <div>
              <strong>Name:</strong> {plantInfo.name}
            </div>
            <div>
              <strong>Species:</strong> {plantInfo.species}
            </div>
            <div>
              <strong>Current Last Watered:</strong>{" "}
              {formatFirebaseTimestamp(plantInfo.currentLastWater)}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
          <button
            onClick={resetWaterNeeds}
            disabled={loading || !plantInfo}
            style={{
              padding: "10px 20px",
              backgroundColor: loading || !plantInfo ? "#ccc" : "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: loading || !plantInfo ? "not-allowed" : "pointer",
              flex: 1,
            }}
          >
            {loading ? "Processing..." : "Reset Water Needs"}
          </button>

          <button
            onClick={resetForm}
            style={{
              padding: "10px 20px",
              backgroundColor: "#f44336",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#ffebee",
              color: "#c62828",
              borderRadius: "4px",
              marginBottom: "20px",
              border: "1px solid #ffcdd2",
            }}
          >
            {error}
          </div>
        )}

        {result && (
          <div
            style={{
              padding: "20px",
              backgroundColor: "#e8f5e8",
              borderRadius: "10px",
              border: "2px solid #4caf50",
            }}
          >
            <h3 style={{ color: "#2e7d32", marginTop: 0 }}>
              Water Needs Reset Successfully! 💧
            </h3>

            <div style={{ marginBottom: "15px" }}>
              <strong>Plant ID:</strong>
              <code
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "2px 6px",
                  borderRadius: "3px",
                  marginLeft: "8px",
                }}
              >
                {result.plantId}
              </code>
            </div>

            <div style={{ marginBottom: "15px" }}>
              <strong>Last Watered Reset To:</strong> {result.resetDate} at{" "}
              {result.resetTime}
            </div>

            <div style={{ marginTop: "15px", fontSize: "14px", color: "#555" }}>
              <strong>Note:</strong> The plant will now appear as if it was last
              watered 1 month ago and may show as needing water.
            </div>
          </div>
        )}

        <div
          style={{
            marginTop: "30px",
            padding: "15px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "8px",
            fontSize: "14px",
          }}
        >
          <strong>⚠️ Important:</strong>
          <ul style={{ marginTop: "8px", paddingLeft: "20px" }}>
            <li>This action cannot be easily undone</li>
            <li>
              Only use valid plant document IDs from the Plants collection
            </li>
            <li>The plant must exist in the database</li>
            <li>This sets the timestamp to exactly 30 days in the past</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PlantWaterReset;
